import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect } from 'react'
import {
  AuftragCheckbox,
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField,
  ReadOnlyText
} from '../Generic'
import type { RadioChipOption } from '../../form'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  Input,
  RadioChipGroup
} from '../../form'
import type { Befehl6FormData } from './schema'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag6_40FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export function Auftrag6_40Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag6_40FormProps>) {
  const formMethods = useFormContext<Befehl6FormData>()
  const { control, formState, watch, setValue } = formMethods
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const x41Enabled = watch('auftrag_6_41.enabled')
  const selector = watch('auftrag_6_40.selector')
  const x47Type = watch('auftrag_6_40.x_47_type')
  const x48Type = watch('auftrag_6_40.x_48_type')
  const x43Betriebsstellenart = watch('auftrag_6_40.x_43_zugmeldestelle.art')
  const x44Betriebsstellenart = watch('auftrag_6_40.x_44_zugmeldestelle.art')

  const clearConflictingX47Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_6_40.x_47_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_6_40.x_47_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const clearConflictingX48Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_6_40.x_48_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_6_40.x_48_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const onX47TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_6_40.x_47_type', newValue as '' | 'km' | 'signal')
      clearConflictingX47Fields(newValue)
    },
    [setValue, clearConflictingX47Fields]
  )

  const onX48TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_6_40.x_48_type', newValue as '' | 'km' | 'signal')
      clearConflictingX48Fields(newValue)
    },
    [setValue, clearConflictingX48Fields]
  )

  useEffect(() => {
    if (!x41Enabled) {
      setValue('auftrag_6_41.x_42_1_kmh', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    }
  }, [x41Enabled, setValue])

  useEffect(() => {
    if (selector === '') {
      setValue('auftrag_6_40.x_43_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_6_40.x_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    } else if (selector === 'in') {
      setValue('auftrag_6_40.x_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_6_40.x_44_zugmeldestelle.art', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    }
  }, [selector, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="6.40"
      fieldName="auftrag_6_40.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-baseline gap-2 md:gap-x-1 md:gap-y-2">
        <div className="flex gap-0.5">
          <div>
            <span data-testid={`${testId}-text`}>
              {t('auftraege.6_40.label')}
            </span>
          </div>
          {(!readOnly || x41Enabled) && (
            <div>
              <span data-testid={`${testId}-text-und`}>
                {t('auftraege.6_40.und_with_brackets')}
              </span>
            </div>
          )}
        </div>
        {(!readOnly || x41Enabled) && (
          <div className="flex flex-wrap items-baseline gap-1">
            <div className="flex flex-nowrap">
              <BefehlFormField
                name="auftrag_6_41.enabled"
                control={control}
                label=""
                className="mr-1"
                readOnly={readOnly}
                disabled={false}
                activateEmptyWarning={false}
                data-testid={`${testId}-auftrag-6-41`}
                render={({ field }) => (
                  <AuftragCheckbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    label={`${field.value ? '6' : 'x'}.41`}
                    readOnly={readOnly}
                    data-testid={`${testId}-auftrag-6-41-inner`}
                  />
                )}
              />
              <span>{t('auftraege.x_41.label')}</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-baseline gap-1">
              <div className="flex flex-col">
                <BefehlFormField
                  name="auftrag_6_41.x_42_1_kmh"
                  control={control}
                  label="km/h"
                  readOnly={readOnly}
                  disabled={!x41Enabled}
                  activateEmptyWarning={activateEmptyFieldWarnings}
                  data-testid={`${testId}-feld-6-42-1`}
                  className="min-w-15"
                  render={({ field, isDisabled }) => (
                    <Input
                      {...field}
                      name="auftrag_6_41.x_42_1_kmh"
                      label="km/h"
                      disabled={isDisabled}
                      errors={errors}
                      data-testid={`${testId}-feld-6-42-1-inner`}
                    />
                  )}
                />
                <span
                  className={x41Enabled ? '' : 'text-gray-500 opacity-60'}
                  data-testid={`${testId}-feld-6-42-1-label`}
                >
                  {x41Enabled ? '6' : 'x'}.42.1 km/h
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-1 flex-1">
        <div className="w-full">
          <div className="flex flex-col gap-1">
            <div className="@container flex flex-col">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 sm:text-right flex justify-start @[400px]:justify-end items-baseline">
                  <BefehlFormField
                    name="auftrag_6_40.selector"
                    control={control}
                    label=""
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-6-43-44-selector`}
                    render={({ field, isDisabled }) => {
                      const options: RadioChipOption[] = [
                        {
                          value: 'zwischen',
                          label: t(
                            'auftraege.6_40_fields.bestimmte_woerter.zwischen'
                          )
                        },
                        {
                          value: 'in',
                          label: t('auftraege.6_40_fields.bestimmte_woerter.in')
                        }
                      ]
                      return (
                        <RadioChipGroup
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          options={options}
                          disabled={isDisabled}
                          readOnly={readOnly}
                          showOnlySelected={readOnly}
                          testId={`${testId}-feld-6-43-44-selector-inner`}
                        />
                      )
                    }}
                  />
                </div>
                <div className="flex-1">
                  {selector === '' ? (
                    <div className="border border-cool-gray-200 px-1 py-1">
                      <ReadOnlyText
                        value={
                          readOnly
                            ? ''
                            : t(
                                'auftraege.6_40_fields.placeholder_select_in_zwischen'
                              )
                        }
                        label=""
                        variant="solid"
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-6-43-44-placeholder`}
                      />
                    </div>
                  ) : (
                    <BorderedBox
                      data-testid={`${testId}-feld-6-43-border-box`}
                      footer={
                        <span data-testid={`${testId}-feld-6-43-label`}>
                          {t('auftraege.6_40.6_43_label')}
                        </span>
                      }
                      className={
                        selector === 'zwischen' ? '@[400px]:border-b-0' : ''
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_6_40.x_43_zugmeldestelle.art"
                            control={control}
                            label={t(
                              'auftraege.1_10.zeile.betriebsstellenart_label'
                            )}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-x_43-betriebsstellenart`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.6_40_fields.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-x_43-betriebsstellenart-select-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_6_40.x_43_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.6_40_fields.x_43_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-x_43-zugmeldestelle`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.6_40_fields.x_43_label')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x43Betriebsstellenart}
                                data-testid={`${testId}-x_43-zugmeldestelle-input-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  )}
                </div>
              </div>

              <div
                className={`${selector === 'zwischen' ? '@container block' : '@container hidden'}`}
              >
                <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                  <div className="@[400px]:w-8 @[400px]:text-right mt-0.5">
                    <span data-testid={`${testId}-text-und-2`}>
                      {t('auftraege.6_40.und')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <BorderedBox
                      data-testid={`${testId}-feld-6-44-border-box`}
                      className="@[400px]:border-t-0"
                      footer={
                        <span data-testid={`${testId}-feld-6-44-label`}>
                          {t('auftraege.6_40.6_44_label')}
                        </span>
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_6_40.x_44_zugmeldestelle.art"
                            control={control}
                            label={t(
                              'auftraege.1_10.zeile.betriebsstellenart_label'
                            )}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-6-44-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.6_40_fields.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-6-44-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_6_40.x_44_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.6_40_fields.x_43_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-feld-6-44-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.6_40_fields.x_43_label')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x44Betriebsstellenart}
                                data-testid={`${testId}-feld-6-44-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  </div>
                </div>
              </div>
            </div>
            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-text-von`}>
                    {t('auftraege.6_40_fields.von_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_6_40.x_47_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-6-47-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_6_40.x_47_type"
                        value={field.value}
                        onChange={(newValue) => onX47TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-6-47-selector-options`}
                      >
                        <AuftragOption
                          value="km"
                          label={t('auftraege.6_40.6_47_1_label')}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_6_40.x_47_1_km"
                                control={control}
                                label={t('auftraege.6_40_fields.km_label')}
                                readOnly={readOnly}
                                disabled={x47Type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-6-47-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.6_40_fields.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-6-47-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption
                          value="signal"
                          label={t('auftraege.6_40.6_47_2_label')}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_6_40.x_47_2_signal"
                                control={control}
                                label={t('auftraege.6_40_fields.signal_label')}
                                readOnly={readOnly}
                                disabled={x47Type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-6-47-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t(
                                      'auftraege.6_40_fields.signal_label'
                                    )}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-6-47-2-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-text-bis`}>
                    {t('auftraege.6_40_fields.bis_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_6_40.x_48_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-6-48-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_6_40.x_48_type"
                        value={field.value}
                        onChange={(newValue) => onX48TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-6-48-selector-options`}
                      >
                        <AuftragOption
                          value="km"
                          label={t('auftraege.6_40.6_48_1_label')}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_6_40.x_48_1_km"
                                control={control}
                                label={t('auftraege.6_40_fields.km_label')}
                                readOnly={readOnly}
                                disabled={x48Type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-6-48-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.6_40_fields.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-6-48-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption
                          value="signal"
                          label={t('auftraege.6_40.6_48_2_label')}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_6_40.x_48_2_signal"
                                control={control}
                                label={t('auftraege.6_40_fields.signal_label')}
                                readOnly={readOnly}
                                disabled={x48Type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-6-48-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t(
                                      'auftraege.6_40_fields.signal_label'
                                    )}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-6-48-2-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
